
import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface SermonPlansLoadingProps {
  isOffline?: boolean;
  onRetry?: () => void;
  error?: Error | null;
}

export function SermonPlansLoading({ isOffline, onRetry, error }: SermonPlansLoadingProps) {
  // If we're offline, show a specific message
  if (isOffline) {
    return (
      <Alert variant="destructive" className="my-6">
        <WifiOff className="h-4 w-4" />
        <AlertTitle>You're offline</AlertTitle>
        <AlertDescription className="space-y-4">
          <p>Cannot load sermon plans while offline. Please check your internet connection.</p>
          {onRetry && (
            <Button onClick={onRetry} variant="outline" size="sm">
              Try Again
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // If there's an error, show it
  if (error) {
    return (
      <Alert variant="destructive" className="my-6">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Connection Error</AlertTitle>
        <AlertDescription className="space-y-4">
          <p>Failed to load sermon plans: {error.message}</p>
          {onRetry && (
            <Button onClick={onRetry} variant="outline" size="sm">
              Try Again
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // Default loading state with skeleton UI
  return (
    <div className="space-y-4 w-full">
      <div className="flex items-center justify-center py-4 w-full mb-4">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-red-500"></div>
      </div>
      
      {/* Skeleton loaders for sermon plans */}
      {[1, 2, 3].map((i) => (
        <div key={i} className="border rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-6 w-24" />
          </div>
          <Skeleton className="h-4 w-1/2 mt-2" />
          <div className="flex justify-between items-center mt-4">
            <Skeleton className="h-4 w-1/4" />
            <div className="flex space-x-2">
              <Skeleton className="h-8 w-8 rounded-md" />
              <Skeleton className="h-8 w-8 rounded-md" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
